import React, {useMemo, useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Table, Icon, Input, Popup, Dropdown, Checkbox} from 'semantic-ui-react';
import {useTable, usePagination, useFilters, useRowSelect} from 'react-table';
import {Filter, DefaultColumnFilter} from '../../../common/components/tableFilters';
import TablePagination from '../../../common/components/reactTablePagination';
import {Actions} from '../../../common/actions';

const {
  addMessageBoxError,
  addInternalUserByGroup,
  addExternalUserByGroup,
  fetchUsersList,
} = Actions;

const AddIcon = ({userForm, userType, group}) => {
  const userEmail = userForm.email.trim();
  const isInvalid = !userForm.name || !userEmail;
  const dispatch = useDispatch();

  const handleAddItem = () => {
    if (userType === 'internal') {
      dispatch(addInternalUserByGroup(group, userForm));
    } else {
      dispatch(addExternalUserByGroup(group, userForm));
    }
  };

  return (
    <Popup
      trigger={
        <Icon
          onClick={handleAddItem}
          disabled={isInvalid}
          color="green"
          bordered
          inverted
          name="add"
        />
      }
      content="Añadir Usuario"
      position="top center"
      inverted
    />
  );
};

const MailUserForm = ({columnsTable, userType, group}) => {
  const userList = useSelector((state) => state.usersList);
  const [userForm, setUserForm] = useState({});
  const dispatch = useDispatch();

  useEffect(() => {
    const userFields = {};
    columnsTable.forEach((column) => {
      const key = column?.accessor || 'action';
      userFields[key] = '';
    });
    setUserForm(userFields);
  }, []);

  const updateUserForm = (e, {name, value, checked}) => {
    if (name === 'status') {
      const status = checked ? 'S' : 'N';
      setUserForm({...userForm, status});
    } else {
      setUserForm({...userForm, [name]: value});
    }
  };

  const handleSearchUsers = (e, {searchQuery}) => {
    const searchUser = searchQuery.toUpperCase();
    dispatch(fetchUsersList({searchUser}));
  };
  
  const selectedInternalUser = (e, {value}) => {
    const userInfo = userList.list.find((user) => user.username === value);
    const {username, name, email, status} = userInfo;
    
    if (userType === 'internal' && (!email || !email.trim())) {
      dispatch(
        addMessageBoxError(
          `El usuario ${username} no cuenta con correo. Porfavor actualizar información`
        )
      );
    }

    setUserForm({...userForm, username, name, email, status});
  };

  const parseUsers = (users) => {
    return users.map((user, index) => ({
      'key': index,
      'text': user.username,
      'value': user.username,
    }));
  };

  const renderCellElement = (name, value) => {
    if (name === 'username' && userType === 'internal') {
      return (
        <Dropdown
          name={name}
          value={value}
          onChange={selectedInternalUser}
          noResultsMessage="No se encontro resultados"
          placeholder="Buscar usuario"
          search
          selection
          loading={userList.loading}
          options={parseUsers(userList.list)}
          onSearchChange={handleSearchUsers}
        />
      );
    } else if (name === 'action') {
      return <AddIcon userType={userType} userForm={userForm} group={group} />;
    } else if (name === 'status') {
      return (
        <Checkbox
          name={name}
          checked={value === 'S' || value === 'A'}
          onChange={updateUserForm}
        />
      );
    } else if (userType === 'external') {
      return (
        <Input name={name} value={value} onChange={updateUserForm} required />
      );
    }
  
    return value;
  };

  return Object.keys(userForm).map((name, idx) => {
    const value = userForm[name];
    return (
      <Table.Cell key={idx} textAlign="center">
        {renderCellElement(name, value)}
      </Table.Cell>
    );
  });
};

const MailGroupTable = (props) => {
  const {list, columnsTableWithProps, isusersList, userType, group} = props;
  const columnsTable = columnsTableWithProps(props);
  const data = useMemo(() => list, list);
  const columns = useMemo(() => columnsTable, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    'state': {pageIndex, pageSize},
  } = useTable(
    {
      columns,
      data,
      'defaultColumn': {'Filter': DefaultColumnFilter},
      'initialState': {'pageIndex': 0, 'pageSize': 10},
      'autoResetFilters': false,
    },
    useFilters,
    usePagination,
    useRowSelect
  );

  return (
    <>
      <div className="table-container">
        <Table className="interactive" striped {...getTableProps()}>
          <Table.Header>
            {headerGroups.map((headerGroup) => (
              <Table.Row key="headers" {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <Table.HeaderCell
                    key={`title-${index}`}
                    textAlign="center"
                    {...column.getHeaderProps()}
                  >
                    {column.render('Header')}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            ))}
            {headerGroups.map((headerGroup) => (
              <Table.Row key="filters" id="filters">
                {headerGroup.headers.map((column, index) => (
                  <Table.HeaderCell key={`filter-${index}`} textAlign="center">
                    <Filter column={column} />
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            ))}
          </Table.Header>
          <Table.Body {...getTableBodyProps()}>
            {!page.length && !isusersList && (
              <Table.Row>
                <Table.Cell colSpan={15} textAlign="center">
                  No hay datos disponibles
                </Table.Cell>
              </Table.Row>
            )}
            {isusersList && (
              <Table.Row>
                <MailUserForm
                  columnsTable={columnsTable}
                  userType={userType}
                  group={group}
                />
              </Table.Row>
            )}
            {page.length > 0 &&
              page.map((row, i) => {
                prepareRow(row);
                return (
                  <Table.Row key={i}>
                    {row.cells.map((cell) => {
                      return (
                        <Table.Cell textAlign="center" {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                );
              })}
          </Table.Body>
        </Table>
      </div>
      <TablePagination
        data={data}
        canPreviousPage={canPreviousPage}
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        pageSize={pageSize}
        canNextPage={canNextPage}
        nextPage={nextPage}
        gotoPage={gotoPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
      />
    </>
  );
};

export default MailGroupTable;
